// -------------------------------------------------------------------------------
// Libraries
// -------------------------------------------------------------------------------
import ReactPlayer from 'react-player';
import { FormattedTime } from 'react-player-controls';
import { useCallback, useRef, useState } from 'react';

// -------------------------------------------------------------------------------
// Store
// -------------------------------------------------------------------------------
import { Paragraph12, Paragraph13 } from '../../theme/theme-provider/typography';

// -------------------------------------------------------------------------------
// Components
// -------------------------------------------------------------------------------
import { PlayIcon, PauseIcon, TrackProgressBar, VolumeOffIcon, VolumeOnIcon } from '../common';

// -------------------------------------------------------------------------------
// Styles and Utils
// -------------------------------------------------------------------------------
import { StyledMusicPlayer } from './style';

// -------------------------------------------------------------------------------
// Component
// -------------------------------------------------------------------------------
export function MusicPlayer({ track, data, firstLoad }) {
  const playerRef = useRef(null);
  const [isPlaying, setPlaying] = useState(true);
  const [isMuted, setMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.5);
  const [progessObj, setProgressObj] = useState({});

  const onChangePlayingStatus = useCallback(() => {
    setPlaying(!isPlaying);
  }, [isPlaying]);

  const onTrackProgressBarChange = useCallback(
    (newPercentValue) => {
      const totalDuration = duration;
      const currentDurationTojump = totalDuration * newPercentValue;

      playerRef.current.seekTo(parseFloat(currentDurationTojump), 'seconds');
    },
    [duration]
  );

  const onVolumeBarChange = useCallback((newPercentValue) => {
    setVolume(newPercentValue);
    setMuted(false);
  }, []);

  const onVolumeStatusChange = useCallback(() => {
    setMuted(!isMuted);
  }, [isMuted]);

  return (
    <StyledMusicPlayer>
      <div className="fr-music-player">
        <ReactPlayer
          ref={playerRef}
          progressInterval={500}
          volume={volume}
          playing={isPlaying}
          muted={isMuted}
          autoPlay={true}
          url={data.mix.default_url}
          className="fr-music-player__player"
          width="100%"
          height="100%"
          onDuration={(d) => setDuration(d)}
          onProgress={(o) => setProgressObj(o)}
        />
        {!firstLoad && (
          <div className="fr-music-player__controls">
            <div className="fr-music-player__controls__track-info">
              <div className="fr-music-player__controls__track-info__image">
                <img src={data.mix.background_image} alt="dj" />
              </div>
              <div className="fr-music-player__controls__track-info__title">
                {track && (
                  <>
                    <Paragraph12>{track.title}</Paragraph12>
                    <Paragraph13>By {track.artist}</Paragraph13>
                  </>
                )}
              </div>
            </div>
            <div className="fr-music-player__controls__player">
              <div className="fr-music-player__controls__player__play">
                <span className="fr-music-player__controls__player__play__play-button" onClick={onChangePlayingStatus}>
                  {isPlaying && <PauseIcon />}
                  {!isPlaying && <PlayIcon />}
                </span>
              </div>
              <div className="fr-music-player__controls__player__progress">
                <FormattedTime numSeconds={progessObj.playedSeconds} />
                <TrackProgressBar
                  value={Math.ceil(progessObj.playedSeconds)}
                  max={Math.ceil(duration)}
                  onChange={onTrackProgressBarChange}
                  transitionDuration={0.8}
                />
                <FormattedTime numSeconds={duration} />
              </div>
            </div>
            <div className="fr-music-player__controls__volume">
              <span className="fr-music-player__controls__volume__action" onClick={onVolumeStatusChange}>
                {(isMuted || volume === 0) && <VolumeOffIcon />}
                {!isMuted && volume > 0 && <VolumeOnIcon />}
              </span>
              <TrackProgressBar
                value={isMuted ? 0 : volume * 100}
                max={100}
                transitionDuration={0.1}
                onChange={onVolumeBarChange}
              />
            </div>
          </div>
        )}
      </div>
    </StyledMusicPlayer>
  );
}

export default MusicPlayer;
